import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Fade,
  Zoom,
  Chip,
  Paper
} from '@mui/material';
import {
  Help as HelpIcon,
  ExitToApp as LogoutIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { IconHeadset, IconStar, IconBell, IconUsersGroup, IconShield, IconMicrophone, IconFileText, IconCalendarEvent, IconBrain } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const WelcomeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weatherInfo, setWeatherInfo] = useState({
    temperature: 0,
    description: '',
    icon: '',
    feels_like: 0,
    humidity: 0,
    wind: 0
  });
  const [userInfo, setUserInfo] = useState({
    name: 'Kullanıcı',
    role: 'agent',
    initials: 'K',
    department: 'Müşteri Hizmetleri'
  });
  const [greeting, setGreeting] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Sayfa yüklendiğinde animasyon için
    setTimeout(() => setLoaded(true), 100);

    // Kullanıcı bilgilerini localStorage'dan al
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setUserInfo({
        name: `${user.firstName} ${user.lastName}`,
        role: user.role,
        initials: `${user.firstName[0]}${user.lastName[0]}`,
        department: user.department || 'Müşteri Hizmetleri'
      });
    }

    // Zamana göre selamlama
    const hour = new Date().getHours();
    if (hour < 6) setGreeting('İyi geceler');
    else if (hour < 12) setGreeting('Günaydın');
    else if (hour < 18) setGreeting('İyi günler');
    else setGreeting('İyi akşamlar');

    // Hava durumu bilgilerini al
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          'https://api.weatherapi.com/v1/current.json?key=ea42dcfa4a2e418ca28135643252409&q=Istanbul&lang=tr'
        );
        if (response.ok) {
          const data = await response.json();
          setWeatherInfo({
            temperature: Math.round(data.current.temp_c),
            description: data.current.condition.text,
            icon: data.current.condition.icon,
            feels_like: Math.round(data.current.feelslike_c),
            humidity: data.current.humidity,
            wind: Math.round(data.current.wind_kph)
          });
        }
      } catch (error) {
        console.log('Hava durumu bilgisi alınamadı:', error);
        setWeatherInfo({
          temperature: 22,
          description: 'Açık',
          icon: '//cdn.weatherapi.com/weather/64x64/day/113.png',
          feels_like: 20,
          humidity: 45,
          wind: 10
        });
      }
    };

    fetchWeather();
  }, []);

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'Sistem Yöneticisi';
      case 'supervisor': return 'Takım Lideri';
      case 'agent': return 'Müşteri Temsilcisi';
      default: return 'Kullanıcı';
    }
  };

  const handleRoleClick = (role: string) => {
    switch (role) {
      case 'agent':
        navigate('/agent-panel');
        break;
      case 'supervisor':
        navigate('/supervisor-dashboard');
        break;
      case 'admin':
        navigate('/admin-options');
        break;
      case 'records':
        navigate('/records');
        break;
      case 'daily':
        navigate('/daily-reports');
        break;
      case 'evaluations':
        navigate('/evaluations');
        break;
      case 'notifications':
        navigate('/notifications');
        break;
      case 'ai':
        navigate('/ai-assistant');
        break;
    }
  };

  const handleLogout = async () => {
    try {
      console.log('🔐 Logout attempt');
      await authService.logout();
      console.log('✅ Logout successful');
      navigate('/login');
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Force logout even if API call fails
      localStorage.clear();
      navigate('/login');
    }
  };

  // Saat güncelleme
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('tr-TR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date: Date) => {
    const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                   'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName}, ${day} ${monthName} ${year}`;
  };

  const roleCards = [
    {
      role: 'agent',
      icon: <IconHeadset size={48} color="#152b69" />,
      title: 'Müşteri Temsilcisi',
      description: 'Müşteri çağrılarını yönet',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      visible: ['agent', 'supervisor', 'admin'].includes(userInfo.role),
      special: false
    },

    {
      role: 'supervisor',
      icon: <IconUsersGroup size={48} color="#152b69" />,
      title: 'Takım Lideri',
      description: 'Ekibi yönet ve raporları incele',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      visible: ['supervisor', 'admin'].includes(userInfo.role),
      special: false
    },
    {
      role: 'admin',
      icon: <IconShield size={48} color="#152b69" />,
      title: 'Sistem Yöneticisi',
      description: 'Sistem ayarlarını ve kullanıcıları yönet',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      visible: userInfo.role === 'admin',
      special: false
    },

  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundImage: 'url(https://images.unsplash.com/photo-1634712282287-14ed57b9cc89?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YXJtY2hhaXJ8ZW58MHx8MHx8fDA%3D)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative',
      overflow: 'hidden',
      transform: 'scaleX(-1)',
    }}>
      {/* İçeriği tekrar aynala (düzelt) */}
      <Box sx={{ transform: 'scaleX(-1)', width: '100%', minHeight: '100vh' }}>
      {/* Animasyonlu arka plan desenleri */}
      <Box sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        zIndex: 0
      }}>
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.03)',
              animation: `float ${20 + i * 5}s infinite ease-in-out`,
              left: `${i * 20}%`,
              top: `${i * 15}%`,
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
                '50%': { transform: 'translateY(-50px) rotate(180deg)' }
              }
            }}
          />
        ))}
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, md: 4 }
      }}>
        {/* Üst Header */}
        <Fade in={loaded} timeout={1000}>
        <Box sx={{ 
          display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 4
          }}>
            {/* Sol - Kullanıcı Bilgileri */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Zoom in={loaded} style={{ transitionDelay: '200ms' }}>
              <Avatar sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "#152b69",
                  fontSize: '2rem',
                fontWeight: 'bold',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                  border: '3px solid rgba(255,255,255,0.9)'
              }}>
                {userInfo.initials}
              </Avatar>
              </Zoom>
              
              <Box>
                <Typography variant="h6" sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 300,
                  mb: 0.5
                }}>
                  {greeting},
                </Typography>
                <Typography variant="h3" sx={{ 
                  color: 'white',
                  fontWeight: 700,
                  mb: 1,
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)'
                }}>
                  {userInfo.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={getRoleDisplayName(userInfo.role)}
                    size="small"
                    sx={{ 
                      background: '#152b69',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid white'
                    }}
                  />
                  <Chip 
                    label={userInfo.department}
                    size="small"
                    sx={{ 
                      background: '#152b69',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid white'
                    }}
                  />
                </Box>
              </Box>
            </Box>
            
            {/* Sağ - Saat ve Hava Durumu */}
            <Box sx={{ textAlign: 'right' }}>
              {/* Dijital Saat */}
              <Paper elevation={0} sx={{ 
                p: 3,
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(20px)',
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.2)',
                mb: 2
              }}>
                <Typography variant="h2" sx={{ 
                  color: 'white',
                  fontWeight: 300,
                  fontFamily: 'monospace',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  mb: 1
                }}>
                  {formatTime(currentTime)}
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'rgba(255,255,255,0.9)',
              display: 'flex', 
              alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <CalendarIcon fontSize="small" />
                  {formatDate(currentTime)}
                </Typography>
              </Paper>

              {/* Hava Durumu Kartı */}
              <Paper elevation={0} sx={{ 
                p: 2,
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(20px)',
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <img 
                    src={weatherInfo.icon} 
                    alt="Hava durumu"
                    style={{ width: '48px', height: '48px' }}
                  />
                  <Box>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 500 }}>
                      {weatherInfo.temperature}°C
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Hissedilen: {weatherInfo.feels_like}°C
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  color: 'rgba(255,255,255,0.9)',
                  mb: 1
                }}>
                  <LocationIcon fontSize="small" />
                  <Typography variant="body2">İstanbul</Typography>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  textTransform: 'capitalize' 
                }}>
                  {weatherInfo.description}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  mt: 1,
                  pt: 1,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Nem: %{weatherInfo.humidity}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Rüzgar: {weatherInfo.wind} km/s
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Fade>

        {/* Rol Kartları */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          paddingLeft: 2,
          paddingTop: 2
        }}>
                <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' },
            gridTemplateRows: { xs: 'auto', md: 'repeat(2, 1fr)' },
            gap: { xs: 0.1, md: 3 },
            maxWidth: '800px',
            width: '100%'
          }}>
            {roleCards.filter(card => card.visible).map((card, index) => (
              <Zoom in={loaded} style={{ transitionDelay: `${400 + index * 100}ms` }} key={card.role}>
                <Box
                  onClick={() => handleRoleClick(card.role)}
                  sx={{
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 1,
                  cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  {card.special ? (
                    <Box sx={{
                      animation: 'goldenPulse 2s ease-in-out infinite',
                      '@keyframes goldenPulse': {
                        '0%': { color: '#FFD700' },
                        '33%': { color: '#FFA500' },
                        '66%': { color: '#DAA520' },
                        '100%': { color: '#FFD700' }
                      }
                    }}>
                      {card.icon}
                    </Box>
                  ) : (
                    card.icon
                  )}
                  
                  <Typography 
                    className="card-title"
                    variant="h6" 
                    sx={{ 
                      fontWeight: 500,
                      color: card.special ? '#FFD700' : 'white', 
                      textAlign: 'center',
                      fontSize: '1rem',
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                      ...(card.special && {
                        animation: 'goldenTextPulse 2s ease-in-out infinite',
                        '@keyframes goldenTextPulse': {
                          '0%': { color: '#FFD700' },
                          '66%': { color: '#DAA520' },
                          '100%': { color: '#FFD700' }
                        }
                      })
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>
              </Zoom>
            ))}
            </Box>
          </Box>
          
        {/* Alt Aksiyonlar */}
        <Fade in={loaded} timeout={1500}>
          <Box sx={{ 
            mt: 0,
            mb: 4,
            display: 'flex',
            gap: 2,
            justifyContent: 'center'
          }}>
            <Button
              variant="contained"
              startIcon={<HelpIcon />}
              sx={{
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                textTransform: 'none',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 500,
                '&:hover': { 
                  background: 'rgba(255,255,255,0.25)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                }
              }}
            >
              Yardım Merkezi
            </Button>
            
            <Button
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                textTransform: 'none',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 500,
                '&:hover': { 
                  background: 'rgba(255,0,0,0.2)',
                  borderColor: 'rgba(255,0,0,0.4)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(255,0,0,0.15)'
                }
              }}
            >
              Oturumu Kapat
            </Button>
          </Box>
        </Fade>
      </Box>
      </Box>
    </Box>
  );
};

export default WelcomeDashboard;